/**
 * Leave drawn over the roster schedule as an OVERLAY, never a plan cell: it lives in its own state and is never passed to `update()`, so a save cannot carry it into `person_day`. Pure so tests can reach it.
 * The type may be absent (`/me/project` rows omit it); a missing type is plain "Leave", never a guess.
 */
import { addDays, daysBetween } from './dates'
import { codeMap } from './derive'
import type { RosterCode } from './types'

type LeaveState = 'approved' | 'pending'

export interface LeaveRow {
  personId?: string
  name: string
  start: string
  end: string
  state: LeaveState
  halfDay: boolean
  type?: string | null
}

export interface LeaveDay {
  state: LeaveState
  halfDay: boolean
  type: string | null
}

export type LeaveIndex = ReadonlyMap<string, LeaveDay>

const key = (name: string, day: string) => `${name}\u0000${day}`

/** The longest window either read route accepts (422 beyond it). */
const MAX_WINDOW_DAYS = 186

/** Split an inclusive window into chunks the server accepts; one oversized request would 422 and silently blank the overlay. */
export function chunkWindow(
  from: string,
  to: string,
  max: number = MAX_WINDOW_DAYS,
): Array<{ from: string; to: string }> {
  if (!from || !to || to < from) return []
  const out: Array<{ from: string; to: string }> = []
  let cursor = from
  while (cursor <= to) {
    const end = addDays(cursor, max - 1)
    const chunkTo = end < to ? end : to
    out.push({ from: cursor, to: chunkTo })
    cursor = addDays(chunkTo, 1)
  }
  return out
}

function daysOf(start: string, end: string): string[] {
  const n = daysBetween(start, end)
  const out: string[] = []
  for (let i = 0; i <= n; i += 1) out.push(addDays(start, i))
  return out
}

/** Approved beats pending when both cover a day. */
function stronger(a: LeaveDay | undefined, b: LeaveDay): LeaveDay {
  if (!a) return b
  if (a.state === 'approved' && b.state !== 'approved') return a
  if (b.state === 'approved' && a.state !== 'approved') return b
  // Same state: a full day beats a half day.
  return a.halfDay && !b.halfDay ? b : a
}

export function buildLeaveIndex(rows: readonly LeaveRow[]): LeaveIndex {
  const index = new Map<string, LeaveDay>()
  for (const row of rows) {
    if (!row.start || !row.end || row.end < row.start) continue
    const entry: LeaveDay = {
      state: row.state,
      // The server sets halfDay only for a one-day request; mirror that so a stray flag cannot turn full days into halves.
      halfDay: row.halfDay && row.start === row.end,
      type: row.type ?? null,
    }
    for (const day of daysOf(row.start, row.end)) {
      const k = key(row.name, day)
      index.set(k, stronger(index.get(k), entry))
    }
  }
  return index
}

export function leaveOn(index: LeaveIndex | undefined, name: string, day: string): LeaveDay | null {
  return index?.get(key(name, day)) ?? null
}

/** Is this person OFF (not cover) that day? Only APPROVED, FULL-DAY leave removes cover. Passed as an optional argument, never by mutating a copy of the document, which could reach `update()` and write leave into plan cells. */
export function onLeaveOff(index: LeaveIndex | undefined, name: string, day: string): boolean {
  const entry = leaveOn(index, name, day)
  return entry !== null && entry.state === 'approved' && !entry.halfDay
}

export type LeaveOff = (name: string, day: string) => boolean

export function leaveOffOf(index: LeaveIndex | undefined): LeaveOff | undefined {
  if (!index || index.size === 0) return undefined
  return (name, day) => onLeaveOff(index, name, day)
}

/** Does a stack hold a WORK code anywhere in it (not only the lead)? Unknown codes are not work. */
export function stackHasWork(stack: readonly string[], codes: readonly RosterCode[]): boolean {
  const map = codeMap(codes as RosterCode[])
  return stack.some((c) => map.get(c)?.cat === 'work')
}

/** A conflict: approved FULL-DAY leave on a day whose plan holds work. */
export function leaveConflict(
  index: LeaveIndex | undefined,
  name: string,
  day: string,
  stack: readonly string[],
  codes: readonly RosterCode[],
): boolean {
  return onLeaveOff(index, name, day) && stackHasWork(stack, codes)
}

export interface LeaveConflictRow {
  name: string
  day: string
}

export function leaveConflicts(
  index: LeaveIndex | undefined,
  names: readonly string[],
  days: readonly string[],
  stackOf: (name: string, day: string) => readonly string[],
  codes: readonly RosterCode[],
): LeaveConflictRow[] {
  if (!index || index.size === 0) return []
  const out: LeaveConflictRow[] = []
  for (const name of names) {
    for (const day of days) {
      if (leaveConflict(index, name, day, stackOf(name, day), codes)) out.push({ name, day })
    }
  }
  return out
}
